/**
 * Live events admin types. Mirror the shapes returned by sporapi's
 * AdminEventResource / AdminRecommendedAccountResource.
 */

import { UserTypes } from "./users.types";

export type EventStatus = "scheduled" | "live" | "ended" | "purged";

/**
 * What participants may post in an event's chat.
 *   mixed      — text and media both allowed (default; how every event
 *                behaved before content modes existed)
 *   media_only — an attachment is mandatory; text is allowed only as
 *                its caption
 *   text_only  — text only; photos and videos are rejected
 */
export type EventContentMode = "mixed" | "media_only" | "text_only";

export interface AdminEvent {
  id: number;
  title: string;
  description: string | null;
  bannerUrl: string | null;
  locationLabel: string;
  centerLat: number;
  centerLng: number;
  radiusMeters: number;
  status: EventStatus;
  contentMode: EventContentMode;
  startsAt: string;
  endsAt: string;
  endedAt: string | null;
  participantsCount: number;
  hostUserId: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Payload for create. All fields required at validation level —
 * matches CreateEventRequest. `banner` is a File the slice sends as
 * multipart/form-data via a FormData wrapper in the mutation.
 */
export interface CreateEventPayload {
  title: string;
  description?: string;
  banner?: File;
  locationLabel: string;
  centerLat: number;
  centerLng: number;
  radiusMeters: number;
  startsAt: string; // ISO 8601
  endsAt: string;
  contentMode?: EventContentMode;
}

/**
 * Same shape as create, all optional — update endpoint accepts a
 * partial. Only `id` is required to identify the row.
 */
/**
 * Content mode is deliberately absent from the update payload: it is the
 * one setting editable after an event goes live, and the general update
 * endpoint refuses every edit past `scheduled`. It has its own mutation.
 */
export interface UpdateEventPayload
  extends Partial<Omit<CreateEventPayload, "contentMode">> {
  id: number;
}

export interface UpdateEventContentModePayload {
  id: number;
  contentMode: EventContentMode;
}

export interface PlaceSuggestion {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface PlaceDetails {
  lat: number;
  lng: number;
  label: string;
}

/**
 * Recommended account strip (#12b). The admin page lists these and
 * lets the user reorder via drag-n-drop or by submitting a new ordered
 * userIds array.
 */
export interface RecommendedAccount {
  id: number;
  position: number;
  user: {
    id: number;
    username: string;
    firstName: string | null;
    lastName: string | null;
    profilePicture: string | null;
    hasVerifiedBadge?: boolean;
    // JURIDICAL accounts display under their company name. Optional —
    // populated once the backend includes them on this payload.
    type?: UserTypes;
    company?: { id: number; name: string; logo?: string | null } | null;
  } | null;
}

export type SanctionType = "ban" | "mute";
export type ModerationRole = "admin" | "moderator";

export interface EventModerator {
  userId: number;
  role: ModerationRole;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  assignedAt: string | null;
}

export interface EventSanction {
  userId: number;
  type: SanctionType;
  expiresAt: string | null;
  reason: string | null;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface EventModerationOverview {
  moderators: EventModerator[];
  sanctions: EventSanction[];
  viewerIsAdmin: boolean;
}

export interface ApplySanctionPayload {
  userId: number;
  type: SanctionType;
  durationSeconds: number | null;
  reason?: string;
}

/**
 * Curated duration set surfaced in the admin UI. Mirrors
 * `EventModerationService::DURATION_PRESETS_SECONDS` on the backend;
 * any value outside this set is rejected at the API boundary.
 */
export const SANCTION_DURATION_PRESETS: ReadonlyArray<{
  label: string;
  seconds: number | null;
}> = [
  { label: "5 minute", seconds: 300 },
  { label: "30 minute", seconds: 1800 },
  { label: "1 oră", seconds: 3600 },
  { label: "24 ore", seconds: 86400 },
  { label: "7 zile", seconds: 604800 },
  { label: "Permanent", seconds: null },
];
